import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import './CourseList.css';

function CourseList({ courses, selectedCourses, handleCourseSelect }) {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedTerm, setSelectedTerm] = useState('all');
  const [selectedYear, setSelectedYear] = useState('all');
  const [englishCourses, setEnglishCourses] = useState([]); // 英語科目を格納するstate
  const [mathCourses, setMathCourses] = useState([]); // 数学科目を格納するstate
  const [showOnlyMajorCourses, setShowOnlyMajorCourses] = useState(true); // 専門科目群のみ表示するか（デフォルト：true）
  const [selectedCalendarCourse, setSelectedCalendarCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState(''); // 検索クエリを格納するstate
  const navigate = useNavigate();
  const location = useLocation();
  const developerMode = location.state?.developerMode;

  useEffect(() => {
    let isMounted = true;

    if (developerMode) {
      const fetchTorifoCodes = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/api/torifo-codes`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const codes = await response.json();
          if (isMounted) {
            // コードを一つずつ順番に選択
            for (const code of codes) {
              if (!selectedCourses.includes(code)) {
                await new Promise(resolve => setTimeout(resolve, 10)); // 10ms待機
                handleCourseSelect(code);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching torifo codes:', error);
          alert('開発者モードのデータ取得に失敗しました。サーバーが起動しているか確認してください。');
        }
      };
      fetchTorifoCodes();
    }

    return () => {
      isMounted = false;
    };
  }, [developerMode]); // selectedCoursesとhandleCourseSelectを依存配列から削除

  useEffect(() => {
    // 英語科目をデータベースから取得
    const fetchEnglishCourses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/english-courses`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setEnglishCourses(data);
      } catch (error) {
        console.error('Error fetching english courses:', error);
        alert('英語科目のデータ取得に失敗しました。サーバーが起動しているか確認してください。');
      }
    };

    fetchEnglishCourses();

    // 数学科目をデータベースから取得
    const fetchMathCourses = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/math-courses`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setMathCourses(data);
      } catch (error) {
        console.error('Error fetching math courses:', error);
        alert('数学科目のデータ取得に失敗しました。サーバーが起動しているか確認してください。');
      }
    };

    fetchMathCourses();
  }, []);

  const handleCheckRequirements = () => {
    navigate('/result');
  };

  const parseTerms = (courses, englishCourses) => {
    // 「前期」「後期」「集中」「通年」の順で重複なく
    const order = ['前期', '後期', '集中', '通年'];
    const termsSet = new Set();
    courses.forEach(course => {
      if (course.term) termsSet.add(course.term);
    });
    englishCourses.forEach(course => {
      if (course.term) termsSet.add(course.term);
    });
    // 指定順で出現するものだけ返す
    return order.filter(term => termsSet.has(term));
  };

  const parseYears = (courses) => {
    const years = new Set();
    courses.forEach(course => {
      if (course.standard_year) {
        years.add(course.standard_year);
      }
    });
    return Array.from(years).sort((a, b) => a - b);
  };

  const parsePeriod = (periodStr) => {
    if (!periodStr) return [];
    // カンマで区切られた時限を配列として処理
    return periodStr.split(',').map(p => {
      const num = parseInt(p.trim());
      return isNaN(num) ? null : num;
    }).filter(num => num !== null);
  };

  const filterCourses = (courses) => {
    // APIから取得した科目から言語科目(英語)を除外
    const allCourses = [...courses];
    return allCourses.filter(course => {
      // 言語科目(英語)を除外
      if (course.faculty_category === '基礎科目群' && course.general_subcategory === '言語科目(英語)') return false;
      
      // 専門科目群のみ表示する場合のフィルタリング（カレンダー表示時のみ）
      if (viewMode === 'calendar' && showOnlyMajorCourses) {
        // 専門科目群のみを表示（専門科目群以外を除外）
        if (course.faculty_category !== '専門科目群') return false;
      }
      
      // 検索クエリによるフィルタリング（リスト表示時のみ）
      if (viewMode !== 'calendar' && searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase().trim();
        const courseName = course.name ? course.name.toLowerCase() : '';
        if (!courseName.includes(query)) return false;
      }
      
      if (selectedTerm !== 'all' && course.term !== selectedTerm) return false;
      // standard_yearフィールドが存在する場合のみフィルタリングを適用
      if (selectedYear !== 'all' && course.standard_year && course.standard_year !== parseInt(selectedYear)) return false;
      return true;
    });
  };

  const getIntensiveCourses = (courses) => {
    return courses.filter(course => course.term === "集中");
  };

  const renderCalendarView = () => {
    const periods = [1, 2, 3, 4, 5, 6];
    const daysOfWeek = ['月', '火', '水', '木', '金'];
    // 通常のフィルタリングを適用（専門科目フィルターは filterCourses 内で処理）
    const filteredCourses = filterCourses(courses);
    const intensiveCourses = getIntensiveCourses(filteredCourses);
    
    return (
      <div className="calendar-view">
        <div className="calendar-grid">
          <div className="calendar-header">
            <div className="time-slot"></div>
            {daysOfWeek.map(day => (
              <div key={day} className="day-header">{day}</div>
            ))}
          </div>
          {periods.map(period => (
            <div key={period} className="calendar-row">
              <div className="time-slot">{period}限</div>
              {daysOfWeek.map(day => (
                <div key={`${day}-${period}`} className="calendar-cell">
                  {filteredCourses
                    .filter(c => {
                      const parsedPeriods = parsePeriod(c.period);
                      return c.day_of_week === day && parsedPeriods.includes(period);
                    })
                    .map(course => (
                      <div
                        key={course.course_code}
                        className={`calendar-course ${selectedCourses.includes(course.course_code) ? 'selected' : ''} ${selectedCalendarCourse?.course_code === course.course_code ? 'highlighted' : ''}`}
                        onClick={() => {
                          handleCourseSelect(course.course_code);
                          setSelectedCalendarCourse(course);
                        }}
                      >
                        <div className="course-name">{course.name}</div>
                        <div className="course-code">{course.course_code}</div>
                        <div className="course-instructor">{course.instructor}</div>
                      </div>
                    ))}
                </div>
              ))}
            </div>
          ))}
        </div>
        {/* 英語科目セクション */}
        {viewMode === 'calendar' && (
          <div className="intensive-courses-section">
            <h3>英語科目</h3>
            <div className="intensive-courses-grid">
              {englishCourses
                .filter(course => {
                  if (!/^E\d{4}/.test(course.course_code)) return false;
                  if (!course.term || !course.standard_year) return false;
                  if (selectedTerm !== 'all' && String(course.term) !== String(selectedTerm)) return false;
                  if (selectedYear !== 'all' && String(course.standard_year) !== String(selectedYear)) return false;
                  return true;
                })
                .map(course => (
                <div
                  key={course.course_code}
                  className={`intensive-course-card ${selectedCourses.includes(course.course_code) ? 'selected' : ''} ${selectedCalendarCourse?.course_code === course.course_code ? 'highlighted' : ''}`}
                  onClick={() => {
                    handleCourseSelect(course.course_code);
                    setSelectedCalendarCourse(
                      selectedCourses.includes(course.course_code) ? null : course
                    );
                  }}
                >
                  <div className="course-header">
                    <h4>{course.name}</h4>
                    <span className="course-code">{course.course_code}</span>
                  </div>
                  <div className="course-details">
                    <div className="detail-row">
                      <span className="detail-label">担当教員</span>
                      <span className="detail-value">{course.instructor}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">開講時期</span>
                      <span className="detail-value">{course.term}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">標準年次</span>
                      <span className="detail-value">{course.standard_year}年次</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* 数学科目セクション */}
        {viewMode === 'calendar' && (
          <div className="intensive-courses-section">
            <h3>数学科目</h3>
            <div className="intensive-courses-grid">
              {mathCourses
                .filter(course => {
                  if (!course.term || !course.standard_year) return false;
                  if (selectedTerm !== 'all' && String(course.term) !== String(selectedTerm)) return false;
                  if (selectedYear !== 'all' && String(course.standard_year) !== String(selectedYear)) return false;
                  return true;
                })
                .reduce((uniqueCourses, course) => {
                  // スペース + 1文字のアルファベットパターンを除去（例：「微分積分I a」→「微分積分I」）
                  const baseName = course.name.replace(/\s[a-zA-Z]$/, '').trim();
                  
                  // 既に同じbaseNameの科目が存在するかチェック
                  const existing = uniqueCourses.find(c => c.name === baseName);
                  
                  if (!existing) {
                    uniqueCourses.push({
                      ...course,
                      name: baseName,
                      instructor: '数学担当教員'
                    });
                  }
                  return uniqueCourses;
                }, [])
                .map(course => (
                <div
                  key={course.course_code}
                  className={`intensive-course-card ${selectedCourses.includes(course.course_code) ? 'selected' : ''} ${selectedCalendarCourse?.course_code === course.course_code ? 'highlighted' : ''}`}
                  onClick={() => {
                    handleCourseSelect(course.course_code);
                    setSelectedCalendarCourse(
                      selectedCourses.includes(course.course_code) ? null : course
                    );
                  }}
                >
                  <div className="course-header">
                    <h4>{course.name}</h4>
                    <span className="course-code">{course.course_code}</span>
                  </div>
                  <div className="course-details">
                    <div className="detail-row">
                      <span className="detail-label">担当教員</span>
                      <span className="detail-value">{course.instructor}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">開講時期</span>
                      <span className="detail-value">{course.term}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">標準年次</span>
                      <span className="detail-value">{course.standard_year}年次</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {intensiveCourses.length > 0 && (
          <div className="intensive-courses-section">
            <h3>集中講義</h3>
            <div className="intensive-courses-grid">
              {intensiveCourses.map(course => (
                <div
                  key={course.course_code}
                  className={`intensive-course-card ${selectedCourses.includes(course.course_code) ? 'selected' : ''} ${selectedCalendarCourse?.course_code === course.course_code ? 'highlighted' : ''}`}
                  onClick={() => {
                    handleCourseSelect(course.course_code);
                    setSelectedCalendarCourse(
                      selectedCourses.includes(course.course_code) ? null : course
                    );
                  }}
                >
                  <div className="course-header">
                    <h4>{course.name}</h4>
                    <span className="course-code">{course.course_code}</span>
                  </div>
                  <div className="course-details">
                    <div className="detail-row">
                      <span className="detail-label">担当教員</span>
                      <span className="detail-value">{course.instructor}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">開講時期</span>
                      <span className="detail-value">{course.term}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">標準年次</span>
                      <span className="detail-value">{course.standard_year}年次</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <h1
            className="header-title clickable-title"
            onClick={() => navigate('/')}
          >
            授業選択システム
          </h1>
          <button
            className="home-button"
            onClick={() => navigate('/')}
          >
            ホームに戻る
          </button>
        </div>
      </header>
      <main className="courses-container">
        <div className="view-controls">
          <div className="view-buttons">
            <button
              className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => {
                setViewMode('grid');
                setSelectedTerm('all');
                setSelectedYear('all');
              }}
            >
              リスト表示
            </button>
            <button
              className={`view-button ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
            >
              時間割表示
            </button>
          </div>
          {viewMode === 'calendar' && (
            <div className="major-courses-filter">
              <label>
                <input
                  type="checkbox"
                  checked={showOnlyMajorCourses}
                  onChange={(e) => setShowOnlyMajorCourses(e.target.checked)}
                />
                専門科目群のみ表示
              </label>
            </div>
          )}
          {viewMode === 'calendar' && (
            <div className="term-controls">
              <div className="filters">
                <div className="filter-group">
                  <span className="filter-label">開講時期</span>
                  <select
                    className="term-select"
                    value={selectedTerm}
                    onChange={(e) => setSelectedTerm(e.target.value)}
                  >
                    <option value="all">全ての学期</option>
                    {parseTerms(courses, englishCourses).map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-group">
                  <span className="filter-label">推奨年次</span>
                  <select
                    className="year-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    <option value="all">全ての学年</option>
                    {parseYears(courses).map(year => (
                      <option key={year} value={year}>{year}年次</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
        <h2>履修可能な授業一覧</h2>
        {/* 検索機能 - リスト表示時のみ表示 */}
        {viewMode !== 'calendar' && (
          <div className="search-container" style={{ marginBottom: '20px' }}>
            <input
              type="text"
              placeholder="授業名で検索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              style={{
                width: '100%',
                maxWidth: '400px',
                padding: '10px 15px',
                borderRadius: '8px',
                border: '1px solid #ddd',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        )}
        {viewMode === 'calendar' ? renderCalendarView() : (
        <div className="courses-grid">
          {filterCourses(courses).map((course) => (
            <div key={course.course_code} className="course-item">
              <label>
                <input
                  type="checkbox"
                  checked={selectedCourses.includes(course.course_code)}
                  onChange={() => handleCourseSelect(course.course_code)}
                />
                <div className="course-info">
                  <h3>{course.name} <span style={{ fontSize: '0.8em', color: '#666' }}>({course.course_code})</span></h3>
                  <p>担当教員: {course.instructor}</p>
                  <p>標準年次: {course.standard_year}</p>
                </div>
              </label>
            </div>
          ))}
          {/* 授業コードがEで始まる英語科目も開講時期・標準年次で絞り込んで表示 */}
          {englishCourses
            .filter(course => {
              if (!/^E\d{4}/.test(course.course_code)) return false;
              if (!course.term || !course.standard_year) return false;
              
              // 検索クエリによるフィルタリング
              if (searchQuery.trim() !== '') {
                const query = searchQuery.toLowerCase().trim();
                const courseName = course.name ? course.name.toLowerCase() : '';
                if (!courseName.includes(query)) return false;
              }
              
              if (selectedTerm !== 'all' && String(course.term) !== String(selectedTerm)) return false;
              if (selectedYear !== 'all' && String(course.standard_year) !== String(selectedYear)) return false;
              return true;
            })
            .map((course) => (
              <div key={course.course_code} className="course-item english-course-item">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course.course_code)}
                    onChange={() => handleCourseSelect(course.course_code)}
                  />
                  <div className="course-info">
                    <h3>{course.name} <span style={{ fontSize: '0.8em', color: '#666' }}>({course.course_code})</span></h3>
                    <p>担当教員: {course.instructor}</p>
                    <p>標準年次: {course.standard_year ? course.standard_year : 2}</p>
                  </div>
                </label>
              </div>
            ))}
          {/* 数学科目をリスト表示にも追加（同名授業統合） */}
          {mathCourses
            .filter(course => {
              if (!course.term || !course.standard_year) return false;
              
              // 検索クエリによるフィルタリング（統合前の名前で検索）
              if (searchQuery.trim() !== '') {
                const query = searchQuery.toLowerCase().trim();
                const courseName = course.name ? course.name.toLowerCase() : '';
                const baseName = course.name ? course.name.replace(/[a-zA-Z]$/, '').toLowerCase() : '';
                if (!courseName.includes(query) && !baseName.includes(query)) return false;
              }
              
              if (selectedTerm !== 'all' && String(course.term) !== String(selectedTerm)) return false;
              if (selectedYear !== 'all' && String(course.standard_year) !== String(selectedYear)) return false;
              return true;
            })
            .reduce((uniqueCourses, course) => {
              // より包括的な同名授業統合（担当教員・開講時期が異なっても統合）
              // スペース + 1文字のアルファベットパターンを除去（例：「微分積分I a」→「微分積分I」）
              const baseName = course.name.replace(/\s[a-zA-Z]$/, '').trim();
              
              // 既に同じbaseNameの科目が存在するかチェック
              const existing = uniqueCourses.find(c => c.name === baseName);
              
              if (!existing) {
                uniqueCourses.push({
                  ...course,
                  name: baseName,
                  instructor: '数学担当教員'
                });
              }
              return uniqueCourses;
            }, [])
            .map((course) => (
              <div key={course.course_code} className="course-item math-course-item">
                <label>
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course.course_code)}
                    onChange={() => handleCourseSelect(course.course_code)}
                  />
                  <div className="course-info">
                    <h3>{course.name.replace(/[a-zA-Z]$/, '')} <span style={{ fontSize: '0.8em', color: '#666' }}>({course.course_code})</span></h3>
                    <p>担当教員: 数学担当教員</p>
                    <p>標準年次: {course.standard_year ? course.standard_year : 1}</p>
                  </div>
                </label>
              </div>
            ))}
        </div>)}
        <div className="selected-courses">
          <h3>選択した授業数: {selectedCourses.length}</h3>
          <button 
            className="check-requirements-button"
            onClick={handleCheckRequirements}
          >
            選択した授業を確認
          </button>
        </div>
      </main>
    </div>
  );
}

export default CourseList;